import React from 'react';
import { Translate } from 'react-redux-i18n';
import get from 'lodash.get';
import dayjs from 'dayjs';
import { 
  Wallet, 
  UserCircle, 
  DollarSign, 
  BookOpen, 
  Target, 
  TrendingUp, 
  Users, 
  Award, 
  Dumbbell, 
  Share2, 
  MessageSquare, 
  Star, 
  Megaphone,
  Trophy
} from 'lucide-react';
import { ViewTable } from '../../components/layouts/viewTable';
import { ViewSectionRow } from '../../components/layouts/viewSectionRow';
import { PageEntityModel } from './types';
import { formatDateTime } from '../../utils/time/formatDateTime';
import { formatTimeElapsed } from '../../utils/time/formatTimeElapsed';

interface PageEntityAchievementsProps {
  entity: PageEntityModel;
  isShowEmptyFields: boolean;
  translationBase: string;
}

const getAchievementIcon = (key: string): React.ReactNode => {
  const iconMap: Record<string, React.ReactNode> = {
    moneyInTheBank: <Wallet size={16} />,
    avatarImageAdded: <UserCircle size={16} />,
    donatedToSelf: <DollarSign size={16} />,
    bloggedToSelf: <BookOpen size={16} />,
    fundraisedToTarget: <Target size={16} />,
    fundraisedToLevel1: <TrendingUp size={16} />,
    oneTwoThreeMembers: <Users size={16} />,
    fundraisedToLevel2: <Award size={16} />,
    workedOut: <Dumbbell size={16} />,
    customFundraised150: <DollarSign size={16} />,
    customFundraised300: <DollarSign size={16} />,
    customSignedUp: <UserCircle size={16} />,
    sharedTheLove: <Share2 size={16} />,
    tellAStory: <MessageSquare size={16} />,
    shootForTheStars: <Star size={16} />,
    theLeadGenerator: <Megaphone size={16} />,
  };
  
  return iconMap[key] || <Trophy size={16} />;
};

export const PageEntityAchievements: React.FC<PageEntityAchievementsProps> = ({ entity, isShowEmptyFields, translationBase }) => {
  const achievements = get(entity, 'achievements', []) as Array<{ key: string; unlockedAt: string }>;
  const sorted = [...achievements].sort((a, b) => dayjs(b.unlockedAt).valueOf() - dayjs(a.unlockedAt).valueOf());
  if (!sorted.length && !isShowEmptyFields) return null;

  return (
    <div className="entity-achievements">
      <ViewTable title={
        <>
          <Translate value={`${translationBase}.achievements.title`} />
          {sorted.length > 0 && ` (${sorted.length})`}
        </>
      }>
        {sorted.length > 0 ? (
          <>
            <ViewSectionRow
              title={`${translationBase}.achievements.badges`}
              data={
                <div className="entity-badges-grid">
                  {sorted.map((a, idx) => {
                    const rel = formatTimeElapsed(a.unlockedAt);
                    const full = formatDateTime(a.unlockedAt);
                    return (
                      <div key={`${a.key}-${idx}`} className="entity-badge" title={`Unlocked: ${full}`}>
                        <div className="entity-badge-icon">
                          {getAchievementIcon(a.key)}
                        </div>
                        <div className="entity-badge-content">
                          <span className="entity-badge-name"><Translate value={`${translationBase}.achievements.${a.key}`} /></span>
                          <span className="entity-badge-time">{rel}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              }
              isShowEmptyFields={isShowEmptyFields}
            />
          </>
        ) : (
          <ViewSectionRow title={`${translationBase}.achievements.badges`} data={<Translate value="common.no-data" />} isShowEmptyFields={isShowEmptyFields} />
        )}
      </ViewTable>
    </div>
  );
};
